<template>
  <footer class="site-footer">
    <div class="container">
      <div class="footer-content">
        <div class="footer-col footer-brand">
          <div class="footer-logo">
            <div class="footer-logo-mark">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"/>
                <line x1="12" y1="22" x2="12" y2="15.5"/>
                <polyline points="22 8.5 12 15.5 2 8.5"/>
              </svg>
            </div>
            <span>{{ siteName }}</span>
          </div>
          <p class="footer-desc">{{ siteDesc }}</p>
          <div class="social-icons">
            <a v-if="socialLinks.wechatQr" :href="socialLinks.wechatQr" target="_blank" title="微信">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.5 19a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM6.5 19a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/><path d="M12 3c-4.97 0-9 3.36-9 7.5 0 2.14 1.16 4.04 3 5.5l-1 3 3.63-1.81c.96.25 1.97.38 2.99.38"/></svg>
            </a>
            <a v-if="socialLinks.weibo" :href="socialLinks.weibo" target="_blank" title="微博">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="2"/><path d="M16 2a10 10 0 0 1 6 6"/><path d="M18 4a6 6 0 0 1 4 4"/><circle cx="12" cy="12" r="10"/></svg>
            </a>
            <a v-if="socialLinks.twitter" :href="socialLinks.twitter" target="_blank" title="Twitter">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/></svg>
            </a>
            <a v-if="socialLinks.facebook" :href="socialLinks.facebook" target="_blank" title="Facebook">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
            </a>
          </div>
        </div>

        <div class="footer-col">
          <h4>{{ locale === 'zh' ? '联系方式' : 'Contact' }}</h4>
          <ul class="contact-list">
            <li>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              <a :href="'mailto:' + contact.email">{{ contact.email }}</a>
            </li>
            <li>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              <span>{{ contact.phone }}</span>
            </li>
            <li v-if="contact.address">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span>{{ contact.address }}</span>
            </li>
          </ul>
        </div>

        <div class="footer-col">
          <h4>{{ locale === 'zh' ? '快速链接' : 'Quick Links' }}</h4>
          <ul class="link-list">
            <li><a href="#home" @click.prevent="scrollTo('home')">{{ locale === 'zh' ? '首页' : 'Home' }}</a></li>
            <li><a href="#about" @click.prevent="scrollTo('about')">{{ locale === 'zh' ? '关于我们' : 'About' }}</a></li>
            <li><a href="#products" @click.prevent="scrollTo('products')">{{ locale === 'zh' ? '产品展示' : 'Products' }}</a></li>
            <li><a href="#contact" @click.prevent="scrollTo('contact')">{{ locale === 'zh' ? '联系我们' : 'Contact' }}</a></li>
            <li><router-link to="/news">{{ locale === 'zh' ? '新闻资讯' : 'News' }}</router-link></li>
          </ul>
        </div>
      </div>

      <div class="footer-bottom">
        <p>{{ copyrightText }}</p>
        <p v-if="icpNumber" class="icp"><a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">{{ icpNumber }}</a></p>
      </div>
    </div>
  </footer>
</template>

<script>
import ThemeManager from '../utils/theme'

export default {
  name: 'Footer',
  props: {
    locale: {
      type: String,
      default: 'zh'
    }
  },
  data() {
    return {
      siteName: ThemeManager.getSiteName(this.locale),
      siteDesc: ThemeManager.getSiteDescription(this.locale),
      contact: ThemeManager.getContactInfo(),
      socialLinks: ThemeManager.getSocialLinks(),
      copyrightText: ThemeManager.getCopyrightText(this.locale),
      icpNumber: ThemeManager.getIcpNumber()
    }
  },
  methods: {
    scrollTo(id) {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    }
  },
  watch: {
    locale(newVal) {
      this.siteName = ThemeManager.getSiteName(newVal)
      this.siteDesc = ThemeManager.getSiteDescription(newVal)
      this.copyrightText = ThemeManager.getCopyrightText(newVal)
    }
  }
}
</script>

<style scoped>
.site-footer {
  background: #0f172a;
  padding: 4rem 0 0;
  margin-top: auto;
  position: relative;
}

.site-footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--primary-color, #1a56db), transparent);
  opacity: 0.3;
}

.footer-content {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  gap: 3rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
}

.footer-logo-mark {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-color, #1a56db);
  color: white;
  border-radius: 6px;
  flex-shrink: 0;
}

.footer-logo span {
  font-size: 1.1rem;
  font-weight: 700;
  color: #f1f5f9;
}

.footer-desc {
  color: #64748b;
  line-height: 1.7;
  font-size: 0.9rem;
  margin-bottom: 1.25rem;
  max-width: 320px;
}

.social-icons {
  display: flex;
  gap: 0.5rem;
}

.social-icons a {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #64748b;
  text-decoration: none;
  transition: all 0.2s ease;
}

.social-icons a:hover {
  background: var(--primary-color, #1a56db);
  color: white;
  border-color: transparent;
}

.footer-col h4 {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 1.25rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.footer-col ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.contact-list li {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
  color: #94a3b8;
  font-size: 0.9rem;
}

.contact-list li svg {
  flex-shrink: 0;
  color: var(--primary-light, #3b82f6);
  opacity: 0.7;
}

.contact-list a {
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.2s ease;
}

.contact-list a:hover {
  color: var(--primary-light, #3b82f6);
}

.link-list li {
  margin-bottom: 0.6rem;
}

.link-list a {
  color: #64748b;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.link-list a::before {
  content: '';
  width: 0;
  height: 1px;
  background: var(--primary-light, #3b82f6);
  transition: width 0.2s ease;
}

.link-list a:hover {
  color: var(--primary-light, #3b82f6);
  padding-left: 0.5rem;
}

.link-list a:hover::before {
  width: 6px;
}

.footer-bottom {
  padding: 1.5rem 0;
  text-align: center;
  color: #475569;
  font-size: 0.85rem;
}

.icp {
  margin-top: 0.25rem;
  font-size: 0.8rem;
}

.icp a {
  color: #64748b;
  text-decoration: none;
  transition: color 0.2s ease;
}

.icp a:hover {
  color: #1e293b;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .site-footer {
    padding: 3rem 0 0;
  }

  .footer-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding-bottom: 2rem;
  }

  .footer-desc {
    max-width: 100%;
  }

  .social-icons {
    justify-content: center;
  }

  .footer-col {
    text-align: center;
  }

  .contact-list li {
    justify-content: center;
  }

  .link-list a:hover {
    padding-left: 0;
  }
}
</style>